import React from 'react';

/**
 * Упрощённая модель hotspot, которую можно использовать поверх Figma embed.
 * В оригинальном проекте easytest-pathway hotspot приходит из proto-файла.
 * В текущем проекте переходы (transitions) маппятся в эту структуру.
 */
export interface HotspotLike {
    id: string;
    frame: string;
    x: number;
    y: number;
    w: number;
    h: number;
    target?: string | null;
}

export interface EventProxyConfig<H extends HotspotLike = HotspotLike> {
    onHotspotClick: (hotspot: H, clickX: number, clickY: number, currentScreenId: string | null) => void;
    onHotspotHoverEnter?: (hotspot: H) => void;
    onHotspotHoverLeave?: (hotspot: H) => void;
    debug?: boolean;
}

/**
 * FigmaEventProxyService — отвечает за рендер прозрачных overlay поверх Figma embed
 * и помогает определить, по какому hotspot кликнул пользователь.
 *
 * Важно: Figma embed сам обрабатывает навигацию по прототипу (через свои hotspot).
 * Мы только перехватываем клики на родительском контейнере в фазе capture
 * и отправляем события в аналитику/состояние приложения.
 */
export class FigmaEventProxyService<H extends HotspotLike = HotspotLike> {
    constructor(config: EventProxyConfig<H>) {
        this.config = config;
    }

    private config: EventProxyConfig<H>;
    private currentScreen: string | null = null;
    private hotspots: Array<H> = [];
    private scale = 1;

    cleanup(): void {
        this.currentScreen = null;
        this.hotspots = [];
    }

    /**
     * Ищет hotspot по координатам клика относительно контейнера.
     * Координаты должны быть в той же системе, в которой заданы hotspot (обычно оригинальные пиксели прототипа).
     */
    findHotspotAt(clickX: number, clickY: number): H | null {
        if (!this.currentScreen) return null;

        const screenHotspots = this.hotspots.filter((h) => h.frame === this.currentScreen);

        for (let i = screenHotspots.length - 1; i >= 0; i--) {
            const h = screenHotspots[i];
            const x = h.x || 0;
            const y = h.y || 0;
            const w = h.w || 0;
            const h_ = h.h || 0;

            if (clickX >= x && clickX <= x + w && clickY >= y && clickY <= y + h_) {
                return h;
            }
        }

        return null;
    }

    getCurrentScreen(): string | null {
        return this.currentScreen;
    }

    getCurrentHotspots(): Array<H> {
        if (!this.currentScreen) return [];
        return this.hotspots.filter((h) => h.frame === this.currentScreen);
    }

    /**
     * Рендерит визуальные overlay поверх iframe.
     * pointerEvents: none — overlay только для визуальной отладки положения hotspot.
     * Клики обрабатываются через Figma Embed Kit 2.0 (postMessage) при наличии client-id.
     */
    renderHotspotOverlays(): Array<React.ReactNode> {
        if (!this.currentScreen || !this.hotspots.length) return [];

        const screenHotspots = this.hotspots.filter((h) => h.frame === this.currentScreen);

        return screenHotspots.map((hotspot, index) => {
            const scale = this.scale || 1;
            const x = (hotspot.x || 0) * scale;
            const y = (hotspot.y || 0) * scale;
            const w = (hotspot.w || 100) * scale;
            const h = (hotspot.h || 50) * scale;

            return React.createElement('div', {
                key: `hotspot-overlay-${hotspot.id}-${index}`,
                style: {
                    position: 'absolute',
                    left: `${x}px`,
                    top: `${y}px`,
                    width: `${w}px`,
                    height: `${h}px`,
                    backgroundColor: this.config.debug ? 'rgba(255, 0, 0, 0.1)' : 'transparent',
                    border: this.config.debug ? '1px solid rgba(255, 0, 0, 0.5)' : 'none',
                    zIndex: 100,
                    cursor: 'pointer',
                    pointerEvents: 'none' as const,
                },
                onMouseEnter: () => {
                    if (this.config.onHotspotHoverEnter) {
                        this.config.onHotspotHoverEnter(hotspot);
                    }
                },
                onMouseLeave: () => {
                    if (this.config.onHotspotHoverLeave) {
                        this.config.onHotspotHoverLeave(hotspot);
                    }
                },
            });
        });
    }

    setScale(scale: number) {
        this.scale = scale || 1;
    }

    /**
     * Обновляет текущий экран и список hotspot.
     * При смене экрана сбрасываем внутреннее состояние.
     */
    updateScreenAndHotspots(currentScreen: string | null, hotspots: Array<H>) {
        this.currentScreen = currentScreen;
        this.hotspots = hotspots;
    }
}
